/**
 * Sales Service
 * 영업 관리 비즈니스 로직
 */

import { SalesRepository } from '../repositories/SalesRepository';
import {
  SalesLead,
  SalesActivity,
  SalesStatus,
  ActivityType,
  SalesStatistics,
  CreateLeadRequest,
  UpdateLeadRequest,
  isLeadActive,
  isLeadContracted,
} from '../models/SalesLead';
import { ServiceType } from '../models';

export class SalesService {
  constructor(private repository: SalesRepository) {}

  // ============================================
  // 목록 조회
  // ============================================

  /**
   * 전체 영업 리드 조회
   */
  async getAllLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const leads = await this.repository.getAllLeads(serviceType);

    // 방어 코드
    if (!Array.isArray(leads)) {
      console.error('getAllLeads: 배열이 아닌 데이터 반환됨:', leads);
      return [];
    }

    // 최신 순 정렬
    return leads.sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
  }

  /**
   * 신규문의 영업 리드 조회 (홈페이지에서 접수된 문의)
   */
  async getNewInquiryLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const leads = await this.repository.getNewInquiryLeads(serviceType);

    if (!Array.isArray(leads)) {
      console.error('getNewInquiryLeads: 배열이 아닌 데이터 반환됨:', leads);
      return [];
    }

    // 최신 순 정렬
    return leads.sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
  }

  /**
   * 상담중 영업 리드 조회 (consulting, quote_sent, negotiating)
   */
  async getConsultingLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const leads = await this.repository.getConsultingLeads(serviceType);

    if (!Array.isArray(leads)) {
      console.error('getConsultingLeads: 배열이 아닌 데이터 반환됨:', leads);
      return [];
    }

    // 최신 순 정렬
    return leads.sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
  }

  /**
   * 계약완료 영업 리드 조회
   */
  async getContractedLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const leads = await this.repository.getContractedLeads(serviceType);

    if (!Array.isArray(leads)) {
      console.error('getContractedLeads: 배열이 아닌 데이터 반환됨:', leads);
      return [];
    }

    // 계약일 최신 순 정렬
    return leads.sort(
      (a, b) =>
        new Date(b.contracted_at || 0).getTime() - new Date(a.contracted_at || 0).getTime()
    );
  }

  /**
   * 내가 담당하는 영업 리드 조회
   */
  async getMyLeads(): Promise<SalesLead[]> {
    return await this.repository.getMyLeads();
  }

  /**
   * 영업 리드 검색
   */
  async searchLeads(query: string): Promise<SalesLead[]> {
    if (!query || query.trim().length === 0) {
      return await this.getAllLeads();
    }
    return await this.repository.searchLeads(query);
  }

  /**
   * 통계 조회
   */
  async getStatistics(): Promise<SalesStatistics> {
    return await this.repository.getStatistics();
  }

  // ============================================
  // 상세 조회 및 생성
  // ============================================

  /**
   * 영업 리드 상세 조회
   */
  async getLead(id: string): Promise<SalesLead> {
    return await this.repository.getLeadById(id);
  }

  /**
   * 영업 리드 생성
   */
  async createLead(request: CreateLeadRequest): Promise<SalesLead> {
    if (!request.company_name || !request.contact_name || !request.contact_phone) {
      throw new Error('업체명, 담당자명, 연락처는 필수입니다.');
    }
    return await this.repository.createLead(request);
  }

  /**
   * 문의에서 영업 리드 생성
   */
  async createLeadFromContact(contactId: string): Promise<SalesLead> {
    if (!contactId) {
      throw new Error('문의 ID가 필요합니다.');
    }
    return await this.repository.createLeadFromContact(contactId);
  }

  // ============================================
  // 수정
  // ============================================

  /**
   * 영업 리드 정보 수정
   */
  async updateLead(id: string, request: UpdateLeadRequest): Promise<SalesLead> {
    return await this.repository.updateLead(id, request);
  }

  /**
   * 상태 변경
   */
  async updateStatus(
    id: string,
    status: SalesStatus,
    reason?: string
  ): Promise<SalesLead> {
    // 영업 실패 시 사유 필수
    if (status === 'lost' && !reason) {
      throw new Error('영업 실패 시 사유를 입력해야 합니다.');
    }
    return await this.repository.updateStatus(id, { status, reason });
  }

  /**
   * 담당자 할당
   */
  async assignLead(
    id: string,
    assigneeId: string,
    assigneeName: string
  ): Promise<SalesLead> {
    if (!assigneeId || !assigneeName) {
      throw new Error('담당자 정보가 필요합니다.');
    }
    return await this.repository.assignLead(id, {
      assignee_id: assigneeId,
      assignee_name: assigneeName,
    });
  }

  /**
   * 계약 완료 처리
   */
  async completeContract(
    id: string,
    organizationId: string,
    contractAmount?: number
  ): Promise<SalesLead> {
    if (!organizationId) {
      throw new Error('사업자 ID가 필요합니다.');
    }

    // 현재 상태 확인
    const lead = await this.repository.getLeadById(id);
    if (isLeadContracted(lead)) {
      throw new Error('이미 계약 완료된 영업 건입니다.');
    }

    return await this.repository.completeContract(id, {
      organization_id: organizationId,
      contract_amount: contractAmount,
    });
  }

  // ============================================
  // 활동
  // ============================================

  /**
   * 활동 목록 조회
   */
  async getActivities(leadId: string): Promise<SalesActivity[]> {
    const activities = await this.repository.getActivities(leadId);

    if (!Array.isArray(activities)) {
      console.error('getActivities: 배열이 아닌 데이터 반환됨:', activities);
      return [];
    }

    // 최신 순 정렬
    return activities.sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
  }

  /**
   * 활동 추가
   */
  async addActivity(
    leadId: string,
    activityType: ActivityType,
    content: string
  ): Promise<SalesActivity> {
    if (!content || content.trim().length === 0) {
      throw new Error('활동 내용을 입력해주세요.');
    }
    return await this.repository.addActivity(leadId, {
      activity_type: activityType,
      content,
    });
  }

  // ============================================
  // 유틸리티
  // ============================================

  /**
   * 클라이언트 사이드 검색
   */
  filterLeads(leads: SalesLead[], query: string): SalesLead[] {
    if (!query || query.trim().length === 0) {
      return leads;
    }

    const lowerQuery = query.toLowerCase();

    return leads.filter(
      (lead) =>
        lead.company_name.toLowerCase().includes(lowerQuery) ||
        lead.contact_name.toLowerCase().includes(lowerQuery) ||
        lead.contact_phone?.includes(query) ||
        lead.contact_email?.toLowerCase().includes(lowerQuery) ||
        lead.notes?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * 상담 중인 영업 건 수 (consulting + quote_sent + negotiating)
   */
  async getConsultingCount(serviceType?: ServiceType): Promise<number> {
    const leads = await this.getConsultingLeads(serviceType);
    return leads.length;
  }

  /**
   * 영업 성공률 계산
   */
  calculateConversionRate(stats: SalesStatistics): number {
    const totalClosed = stats.contracted + stats.lost;
    if (totalClosed === 0) return 0;
    return Math.round((stats.contracted / totalClosed) * 100);
  }

  /**
   * 평균 계약 금액 계산
   */
  calculateAverageContractAmount(stats: SalesStatistics): number {
    if (stats.contracted === 0) return 0;
    return Math.round(stats.total_contract_amount / stats.contracted);
  }
}
